import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { AfterViewChecked, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostBinding, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { EllipsisWithTooltipDirective } from '../../../../directives/ellipsis-with-tooltip.directive';
import { IRadioEprEventEx } from '../../../../models/patient-history.models';
import { seTimelineComponentSetSelectedItem } from '../../../../se-timeline/redux/se-timeline.actions';
import { UserConfigurationSettingsService } from '../../../../services/settings/user-configuration-settings.service';
import { HistoryItemHelperService } from '../../services/history-item-helper.service';
import { IReportHistoryEprEvent } from '../models/se-side-report-history.models';


@Component({
    selector: 'se-side-report-history-item',
    animations: [
        trigger('expansionAnimation', [
            transition(':enter', [style({ opacity: 0 }), animate('400ms ease-in', style({ opacity: 1 }))]),
            transition(':leave', [animate('400ms ease-out', style({ opacity: 0 }))]),
        ]),
    ],
    standalone: true,
    templateUrl: './se-side-report-history-item.component.html',
    styleUrls: ['./se-side-report-history-item.component.scss'],
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        EllipsisWithTooltipDirective,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SeSideReportHistoryItemComponent implements OnInit, AfterViewChecked {
    @ViewChild('reportCard') selectedCard!: ElementRef;
    @HostBinding('style') style = {};
    @Input() expandedItemId: string | null = null;
    @Input() isCp: boolean = false;
    @Input() item!: IReportHistoryEprEvent | any;
    @Output() historySelectionChanged = new EventEmitter<IReportHistoryEprEvent>();

    isAuthorized: boolean = true;
    private lastExpandedItemId: string | null = null;
    timelineItemSelectionSubscription?: Subscription;
    get isExpanded(): boolean {
        return this.expandedItemId === this.item.id;
    }
    constructor(
        private actions: Actions,
        private historyItemHelperService: HistoryItemHelperService,
        private userConfigurationSettingsService: UserConfigurationSettingsService
    ) {
    }

    ngOnInit(): void {
        this.listenForStoreEvents();
        this.isAuthorized = this.userConfigurationSettingsService.isAuthorized(['viewer_Get'], 'single');
    }

    ngAfterViewChecked() {
        if (this.isExpanded && this.lastExpandedItemId !== this.item.id) {
            this.lastExpandedItemId = this.item.id;
            setTimeout(() => {
                this.selectedCard?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        }
    }

    toggleExpanded() {
        if (this.isExpanded) {
            this.historySelectionChanged.emit({ ...this.item, selected: false });
        } else {
            this.historySelectionChanged.emit(this.item);
        }
    }

    onCardSelected() {
        if (!this.item.selected) {
            this.item.selected = true;
            this.historySelectionChanged.emit(this.item);
        }
    }

    copyReport(event: any, item: IRadioEprEventEx) {
        this.historyItemHelperService.copyReport(event, item);
    }

    openPdf(event: MouseEvent) {
        this.onCardSelected();
        this.historyItemHelperService.openPdf(event, this.item);
    }

    listenForStoreEvents() {
        this.timelineItemSelectionSubscription = this.actions
            .pipe(ofType(seTimelineComponentSetSelectedItem))
            .subscribe((action) => {
                const shouldBeExpanded = action.selectedItem === this.item.id;
                if (shouldBeExpanded) {
                    if (this.item.selected) return;
                    if (this.selectedCard?.nativeElement) {
                        this.selectedCard.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                    this.historySelectionChanged.emit(this.item);
                } else {
                    this.item.selected = false;
                }
            });
    }

    async openImagesByEprEvent(event: MouseEvent) {
        this.onCardSelected();
        this.historyItemHelperService.openImagesByEprEvent(event, this.item);
    }
}
